import { createSlice, createSelector } from "@reduxjs/toolkit";
import { ofType } from "redux-observable";
import { of, from } from "rxjs";
import { switchMap, map, catchError, mergeMap, filter } from "rxjs/operators";
import { selectAllCountriesEntities } from "@nxplatform/geo-data/slices/all-countries.slice";
import { getIpDataState } from "@nxplatform/geo-data/slices/ip-data.slice";
import { selfExclusion, customerLastConnectionInfo } from "@nxplatform/helpers/constants/customer";
import { AvailablePlatformName } from "@nxplatform/helpers/entities";
import { getLocaleSetupState } from "@nxplatform/platform-settings/locale-setup.selectors";
import { customerForgotPasswordFailedClear, changeSmarticoInitStatus, getCustomerTokenSuccess } from "@nxplatform/store-shared/customer/shared-customer-actions";
import { CUSTOMER_NO_AUTH_FEATURE_KEY } from "@nxplatform/store-shared/customer/shared-customer-feature-keys";
import { getCustomerState } from "@nxplatform/store-shared/customer/shared-customer-selectors";
import { AppStorage } from "@service/api-client/http/helpers/AppStorage";
import { selectIsUserRestrictionsAvailable } from "@nxplatform/platform-settings/platform-settings.selectors";
import { SmarticoBlockType } from "@nxplatform/platform-entities/smartico/smartico-entities";
const initialSmarticoPopupsBlocked = {
  [SmarticoBlockType.Pathname]: false,
  [SmarticoBlockType.Cashier]: false,
  [SmarticoBlockType.Game]: false
};
const initialCustomNoAuthState = {
  jwtError: null,
  jwtLoaded: false,
  lastConnectionInfoBarVisible: !!Number(AppStorage.get(customerLastConnectionInfo)),
  refreshTokenLoaded: false,
  restorePasswordTokenLoaded: false,
  restorePasswordTokenError: null,
  unavailableUntil: null,
  passwordError: null,
  passwordForgotError: null,
  passwordLoaded: false,
  latrobetSessionIdLoaded: null,
  latrobetSessionId: null,
  selfExclusionVisible: !!AppStorage.get(selfExclusion),
  isSmarticoLoaded: false,
  smarticoPopupsBlocked: initialSmarticoPopupsBlocked,
  pauseRouteRendering: false
};
const customerNoAuthSlice = createSlice({
  name: CUSTOMER_NO_AUTH_FEATURE_KEY,
  initialState: initialCustomNoAuthState,
  reducers: {
    getRegisterReferralProgramClickStart: () => {
    },
    getRegisterReferralProgramClickSuccess: () => {
    },
    getRegisterReferralProgramClickFailure: () => {
    },
    customerRefreshStart: () => {
    },
    customerRefreshFail: (state, action) => {
    },
    customerRefreshSuccess: (state, action) => {
    },
    jwtFailure: (state) => {
      state.jwtError = true;
      state.pauseRouteRendering = false;
    },
    jwtSuccess: (state) => {
      state.jwtError = false;
      state.jwtLoaded = true;
      state.pauseRouteRendering = false;
    },
    jwtStart: (state) => {
      state.pauseRouteRendering = true;
      state.jwtError = initialCustomNoAuthState.jwtError;
      state.jwtLoaded = false;
      state.pauseRouteRendering = true;
    },
    setCustomerTokenStart: () => {
    },
    setCustomerTokenSuccess: () => {
    },
    setCustomerTokenFail: () => {
    },
    setLastConnectionInfoBarVisibility: (state, action) => {
      state.lastConnectionInfoBarVisible = action.payload;
      AppStorage.set(customerLastConnectionInfo, Number(action.payload));
    },
    refreshTokenStart: (state, action) => {
      state.refreshTokenLoaded = false;
    },
    customerCheckRestorePasswordTokenSuccess: (state, action) => {
      state.restorePasswordTokenLoaded = true;
    },
    customerCheckRestorePasswordTokenFailed: (state, action) => {
      const [firstMessage] = action.payload.messages;
      state.restorePasswordTokenError = firstMessage;
      state.restorePasswordTokenLoaded = true;
    },
    customerForgotPasswordStart: (state, action) => {
      state.passwordLoaded = false;
      state.passwordForgotError = initialCustomNoAuthState.passwordForgotError;
    },
    customerForgotPasswordSuccess: (state, action) => {
      state.passwordLoaded = true;
      if (action.payload.unavailableForSeconds) {
        state.unavailableUntil = (/* @__PURE__ */ new Date()).getTime() + action.payload.unavailableForSeconds * 1e3;
      }
    },
    customerForgotPasswordFailed: (state, action) => {
      const [firstMessage] = action.payload.messages;
      state.passwordForgotError = firstMessage;
    },
    customerCheckRestorePasswordTokenStart: (state, action) => {
      state.restorePasswordTokenLoaded = false;
      state.restorePasswordTokenError = initialCustomNoAuthState.restorePasswordTokenError;
    },
    getLatrobetSessionIdStart: (state) => {
      state.latrobetSessionIdLoaded = true;
    },
    getLatrobetSessionIdSucces: (state, action) => {
      state.latrobetSessionId = action.payload.sessionId;
      state.latrobetSessionIdLoaded = false;
    },
    getLatrobetSessionIdFailed: () => {
    },
    clearLatrobetSessionId: (state) => {
      state.latrobetSessionId = initialCustomNoAuthState.latrobetSessionId;
      state.latrobetSessionIdLoaded = initialCustomNoAuthState.latrobetSessionIdLoaded;
    },
    clearPasswordLoadedState: (state) => {
      state.passwordLoaded = initialCustomNoAuthState.passwordLoaded;
    },
    setSelfExclusionVisibility: (state, action) => {
      state.selfExclusionVisible = action.payload;
    },
    changeSmarticoPopupsBlocked: (state, action) => {
      state.smarticoPopupsBlocked[action.payload.blockType] = action.payload.value;
    }
  },
  extraReducers: (builder) => {
    builder.addCase(customerForgotPasswordFailedClear, (state) => {
      state.passwordForgotError = initialCustomNoAuthState.passwordForgotError;
    });
    builder.addCase(changeSmarticoInitStatus, (state, action) => {
      state.isSmarticoLoaded = action.payload;
    });
  }
});
const customerNoAuthReducer = customerNoAuthSlice.reducer;
const {
  setSelfExclusionVisibility,
  clearPasswordLoadedState,
  getLatrobetSessionIdStart,
  getLatrobetSessionIdSucces,
  getLatrobetSessionIdFailed,
  clearLatrobetSessionId,
  getRegisterReferralProgramClickStart,
  getRegisterReferralProgramClickSuccess,
  getRegisterReferralProgramClickFailure,
  customerRefreshStart,
  customerRefreshFail,
  customerRefreshSuccess,
  jwtFailure,
  jwtSuccess,
  jwtStart,
  setCustomerTokenStart,
  setCustomerTokenSuccess,
  setCustomerTokenFail,
  setLastConnectionInfoBarVisibility,
  customerForgotPasswordStart,
  customerForgotPasswordSuccess,
  customerForgotPasswordFailed,
  customerCheckRestorePasswordTokenSuccess,
  customerCheckRestorePasswordTokenFailed,
  customerCheckRestorePasswordTokenStart,
  changeSmarticoPopupsBlocked
} = customerNoAuthSlice.actions;
const getCustomerNoAuthState = (rootState) => rootState[CUSTOMER_NO_AUTH_FEATURE_KEY];
const selectSmarticoPopupsBlocked = createSelector(
  getCustomerNoAuthState,
  (s) => s.smarticoPopupsBlocked
);
const selectLatrobetSessionIdLoaded = createSelector(
  getCustomerNoAuthState,
  (s) => s.latrobetSessionIdLoaded
);
const selectLatrobetSessionId = createSelector(
  getCustomerNoAuthState,
  (s) => s.latrobetSessionId
);
const selectCustomerSelfExclusionState = createSelector(
  getCustomerNoAuthState,
  (s) => s.selfExclusionVisible
);
const selectSmarticoInitStatus = createSelector(
  getCustomerNoAuthState,
  (s) => s.isSmarticoLoaded
);
const getLatrobetSessionId = (userId) => ({
  type: getLatrobetSessionIdStart.type,
  payload: userId
});
const getLatrobetSessionIdEpic = (action$, state$, {
  Api
}) => action$.pipe(
  ofType(getLatrobetSessionIdStart.type),
  switchMap((action) => Api.customerNoAuth.getLatrobetSessionId(action.payload).pipe(
    map((data) => getLatrobetSessionIdSucces(data)),
    catchError(() => of(getLatrobetSessionIdFailed()))
  ))
);
const loginCustomerViaJWTToken = (tokens) => ({
  type: jwtStart.type,
  payload: tokens
});
const registerReferralProgramClickEpic = (action$, state$, { Api }) => action$.pipe(
  ofType(getRegisterReferralProgramClickStart.type),
  switchMap((action) => Api.customerNoAuth.registerReferralProgramClick(action.payload).pipe(
    map(() => getRegisterReferralProgramClickSuccess()),
    catchError(() => of(getRegisterReferralProgramClickFailure()))
  ))
);
const registerReferralProgramClick = (referralInfo) => ({
  type: getRegisterReferralProgramClickStart.type,
  payload: referralInfo
});
const customerRefreshEpic = (action$, state$, { Api }) => action$.pipe(
  ofType(customerRefreshStart.type),
  switchMap(() => Api.customerNoAuth.customerRefresh().pipe(
    map((data) => customerRefreshSuccess(data)),
    catchError((err) => of(customerRefreshFail(err)))
  ))
);
const customerRefresh = () => ({ type: customerRefreshStart.type });
const isDisabledVip = createSelector(
  [
    getLocaleSetupState,
    getIpDataState,
    getCustomerState,
    selectAllCountriesEntities,
    selectIsUserRestrictionsAvailable
  ],
  (s, ipData, customer, countriesList, userRestrictionsAvailable) => {
    const { entities: customerData } = customer;
    const { userRestrictions } = ipData.customer || {};
    const { country: countryData } = customerData || {};
    if (countryData && s.env.platformName !== AvailablePlatformName.tonyNL) {
      const country = countriesList.find((c) => c.alpha_2 === countryData.code);
      return !!((country == null ? void 0 : country.isBlockedVip) || userRestrictionsAvailable && userRestrictions.promo.isBlockedVip);
    }
    const { internalCountry } = ipData.entities || {};
    const { isBlockedVip } = internalCountry || {};
    return !!(isBlockedVip || userRestrictionsAvailable && userRestrictions.promo.isBlockedVip);
  }
);
const setWScustomerToken = (token) => ({
  type: setCustomerTokenStart.type,
  payload: token
});
const selectLastConnectionBarVisibleState = createSelector(
  getCustomerNoAuthState,
  (s) => s.lastConnectionInfoBarVisible
);
const selectIsRouteRenderingPaused = createSelector(
  getCustomerNoAuthState,
  ({ pauseRouteRendering }) => pauseRouteRendering
);
const selectJwtFailure = createSelector(
  getCustomerNoAuthState,
  (s) => s.jwtError
);
const setWSCustomerTokenEpic = (action$, state$, { Api }) => action$.pipe(
  ofType(setCustomerTokenStart.type),
  filter(() => !!Api.wsPrivateClient),
  switchMap((action) => Api.wsPrivateClient.tokenUpdate({ Authorization: `Bearer ${action.payload}` })),
  map(() => setCustomerTokenSuccess()),
  catchError(() => of(setCustomerTokenFail()))
);
const loginCustomerViaJWTTokenEpic = (action$, state$, { Api }) => action$.pipe(
  ofType(jwtStart.type),
  switchMap((action) => from(Api.customerNoAuth.customerJWTLogin(action.payload)).pipe(
    mergeMap((data) => of(getCustomerTokenSuccess(data), jwtSuccess())),
    catchError(() => of(jwtFailure()))
  ))
);
const selectCustomerForgotError = createSelector(
  getCustomerNoAuthState,
  (s) => s.passwordForgotError
);
const selectCustomerPasswordUnavailableUntil = createSelector(
  getCustomerNoAuthState,
  (s) => s.unavailableUntil
);
const selectCustomerRestorePasswordTokenError = createSelector(
  getCustomerNoAuthState,
  (s) => s.restorePasswordTokenError
);
const selectCustomerForgotPasswordRequestLoaded = createSelector(
  getCustomerNoAuthState,
  (s) => s.passwordLoaded
);
const selectCustomerRestorePasswordTokenLoaded = createSelector(
  getCustomerNoAuthState,
  (s) => s.restorePasswordTokenLoaded
);
const customerForgotPassword = (data) => ({
  type: customerForgotPasswordStart.type,
  payload: data
});
const customerForgotPasswordEpic = (action$, state$, { Api }) => action$.pipe(
  ofType(customerForgotPasswordStart.type),
  switchMap((action) => Api.customerNoAuth.passwordRestore(action.payload).pipe(
    map((data) => customerForgotPasswordSuccess(data)),
    catchError((err) => of(customerForgotPasswordFailed(err)))
  ))
);
const customerCheckRestorePasswordToken = (token) => ({
  type: customerCheckRestorePasswordTokenStart.type,
  payload: token
});
const customerCheckRestorePasswordTokenEpic = (action$, state$, { Api }) => action$.pipe(
  ofType(customerCheckRestorePasswordTokenStart.type),
  switchMap((action) => Api.customerNoAuth.getRestorePasswordTokenCheck(action.payload).pipe(
    map((data) => customerCheckRestorePasswordTokenSuccess(data)),
    catchError((err) => of(customerCheckRestorePasswordTokenFailed(err)))
  ))
);
export {
  changeSmarticoPopupsBlocked,
  clearLatrobetSessionId,
  clearPasswordLoadedState,
  customerCheckRestorePasswordToken,
  customerCheckRestorePasswordTokenEpic,
  customerCheckRestorePasswordTokenFailed,
  customerCheckRestorePasswordTokenStart,
  customerCheckRestorePasswordTokenSuccess,
  customerForgotPassword,
  customerForgotPasswordEpic,
  customerForgotPasswordFailed,
  customerForgotPasswordStart,
  customerForgotPasswordSuccess,
  customerNoAuthReducer,
  customerNoAuthSlice,
  customerRefresh,
  customerRefreshEpic,
  customerRefreshFail,
  customerRefreshStart,
  customerRefreshSuccess,
  getCustomerNoAuthState,
  getLatrobetSessionId,
  getLatrobetSessionIdEpic,
  getLatrobetSessionIdFailed,
  getLatrobetSessionIdStart,
  getLatrobetSessionIdSucces,
  getRegisterReferralProgramClickFailure,
  getRegisterReferralProgramClickStart,
  getRegisterReferralProgramClickSuccess,
  initialCustomNoAuthState,
  initialSmarticoPopupsBlocked,
  isDisabledVip,
  jwtFailure,
  jwtStart,
  jwtSuccess,
  loginCustomerViaJWTToken,
  loginCustomerViaJWTTokenEpic,
  registerReferralProgramClick,
  registerReferralProgramClickEpic,
  selectCustomerForgotError,
  selectCustomerForgotPasswordRequestLoaded,
  selectCustomerPasswordUnavailableUntil,
  selectCustomerRestorePasswordTokenError,
  selectCustomerRestorePasswordTokenLoaded,
  selectCustomerSelfExclusionState,
  selectIsRouteRenderingPaused,
  selectJwtFailure,
  selectLastConnectionBarVisibleState,
  selectLatrobetSessionId,
  selectLatrobetSessionIdLoaded,
  selectSmarticoInitStatus,
  selectSmarticoPopupsBlocked,
  setCustomerTokenFail,
  setCustomerTokenStart,
  setCustomerTokenSuccess,
  setLastConnectionInfoBarVisibility,
  setSelfExclusionVisibility,
  setWSCustomerTokenEpic,
  setWScustomerToken
};
